import { useMemo, useReducer } from 'react';
import type {
    AssessmentResultSortingRule,
    TraineeReportFilterInput,
    TraineeReportSortingRule,
} from '@/apolloClient.types';
import {
    AssessmentResultSortingField,
    TraineeReportSortingField,
    SortingOrder as GqlSortingOrder,
} from '@/apolloClient.types';
import type { SortOrder as TableSortOrderValue } from '@/components/common/Table/types';
import { SortingOrder as TableSortingOrder } from '@/components/common/Table/types';

// Per-tab sort rule types — no casting needed in list components
export type TraineeSortRule = TraineeReportSortingRule & { orderValue: TableSortOrderValue | undefined };
export type AssessmentResultSortRule = AssessmentResultSortingRule & { orderValue: TableSortOrderValue | undefined };

export enum TabKey {
    Overall = 'overall',
    TeamResilience = 'teamResilience',
    PersonalCoping = 'personalCoping',
}

export interface TabSortState {
    overall: TraineeSortRule;
    teamResilience: AssessmentResultSortRule;
    personalCoping: AssessmentResultSortRule;
}

export enum ViewType {
    Listing = 'listing',
    ShowReport = 'showReport',
    DownloadReport = 'downloadReport',
}

export interface State {
    page: number;
    pageSize: number;
    sort: TabSortState;
    filter: TraineeReportFilterInput;
    viewType: ViewType;
    assessmentRunIdSelected?: string;
    userIdSelected?: string;
    programIdSelected?: string;
    editMode: boolean;
    programNameSelected?: string;
    completedDate?: Date;
    startDateSelected?: Date;
    endDateSelected?: Date;
    traineeName?: string;
    downloadingRowId?: string;
    isDownloadInProgress?: boolean;
}

export interface SetPageAction {
    type: 'SET_PAGE';
    page: number;
}
export interface SetPageSizeAction {
    type: 'SET_PAGE_SIZE';
    pageSize: number;
}
export interface SetSortAction {
    type: 'SET_SORT';
    tab: TabKey;
    sortBy: TraineeSortRule | AssessmentResultSortRule;
}
export interface SetFilterAction {
    type: 'SET_FILTER';
    filter: TraineeReportFilterInput;
}
export interface SetViewAction {
    type: 'SET_VIEW';
    payload: {
        viewType: ViewType;
        assessmentRunId?: string;
        userId?: string;
        traineeName?: string;
        programId?: string;
        programName?: string;
        startDate?: Date;
        endDate?: Date;
        completedDate?: Date;
        editMode?: boolean;
        downloadingRowId?: string;
        isDownloadInProgress?: boolean;
    };
}
export interface ResetViewAction {
    type: 'RESET_VIEW';
}

export type Action =
    | SetPageAction
    | SetPageSizeAction
    | SetSortAction
    | SetFilterAction
    | SetViewAction
    | ResetViewAction;

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'SET_PAGE':
            return { ...state, page: action.page };

        case 'SET_PAGE_SIZE':
            // Reset to page 1 when page size changes
            return { ...state, page: 1, pageSize: action.pageSize };

        case 'SET_SORT':
            // Reset to page 1 when sort changes, update only the target tab's sort
            return { ...state, page: 1, sort: { ...state.sort, [action.tab]: action.sortBy } };

        case 'SET_FILTER':
            // Reset to page 1 when filter changes
            return { ...state, page: 1, filter: action.filter };

        case 'SET_VIEW':
            return {
                ...state,
                viewType: action.payload.viewType,
                assessmentRunIdSelected: action.payload.assessmentRunId ?? state.assessmentRunIdSelected,
                userIdSelected: action.payload.userId ?? state.userIdSelected,
                programIdSelected: action.payload.programId ?? state.programIdSelected,
                programNameSelected: action.payload.programName ?? state.programNameSelected,
                startDateSelected: action.payload.startDate ?? state.startDateSelected,
                endDateSelected: action.payload.endDate ?? state.endDateSelected,
                editMode: action.payload.editMode ?? state.editMode,
                traineeName: action.payload.traineeName ?? state.traineeName,
                completedDate: action.payload.completedDate ?? state.completedDate,
                downloadingRowId: action.payload.downloadingRowId ?? state.downloadingRowId,
                isDownloadInProgress: action.payload.isDownloadInProgress ?? state.isDownloadInProgress,
            };

        case 'RESET_VIEW':
            return {
                ...state,
                viewType: ViewType.Listing,
                assessmentRunIdSelected: undefined,
                userIdSelected: undefined,
                programIdSelected: undefined,
                editMode: false,
                programNameSelected: undefined,
                startDateSelected: undefined,
                endDateSelected: undefined,
                traineeName: undefined,
                completedDate: undefined,
                downloadingRowId: undefined,
                isDownloadInProgress: undefined,
            };

        default:
            return state;
    }
};

const defaultTraineeSort: TraineeSortRule = {
    field: TraineeReportSortingField.Programme,
    order: GqlSortingOrder.Asc,
    orderValue: TableSortingOrder.Asc,
};

const defaultAssessmentResultSort: AssessmentResultSortRule = {
    field: AssessmentResultSortingField.Programme,
    order: GqlSortingOrder.Asc,
    orderValue: TableSortingOrder.Asc,
};

export const initialState: State = {
    page: 1,
    pageSize: 10,
    sort: {
        overall: defaultTraineeSort,
        teamResilience: defaultAssessmentResultSort,
        personalCoping: defaultAssessmentResultSort,
    },
    filter: {},
    viewType: ViewType.Listing,
    assessmentRunIdSelected: undefined,
    userIdSelected: undefined,
    programIdSelected: undefined,
    editMode: false,
    programNameSelected: undefined,
    startDateSelected: undefined,
    endDateSelected: undefined,
    traineeName: undefined,
    completedDate: undefined,
    downloadingRowId: undefined,
    isDownloadInProgress: undefined,
};

export interface ReportActions {
    setPage: (page: number) => void;
    setPageSize: (pageSize: number) => void;
    setSort: (tab: TabKey, sortBy: TraineeSortRule | AssessmentResultSortRule) => void;
    setFilter: (filter: TraineeReportFilterInput) => void;
    setView: (payload: {
        viewType: ViewType;
        assessmentRunId?: string;
        userId?: string;
        traineeName?: string;
        programId?: string;
        programName?: string;
        startDate?: Date;
        endDate?: Date;
        completedDate?: Date;
        editMode?: boolean;
        downloadingRowId?: string;
        isDownloadInProgress?: boolean;
    }) => void;
    resetView: () => void;
}

export const useReportState = () => {
    const [state, dispatch] = useReducer(reducer, initialState);

    const actions = useMemo<ReportActions>(
        () => ({
            setPage: page => dispatch({ type: 'SET_PAGE', page }),
            setPageSize: pageSize => dispatch({ type: 'SET_PAGE_SIZE', pageSize }),
            setSort: (tab, sortBy) => dispatch({ type: 'SET_SORT', tab, sortBy }),
            setFilter: filter => dispatch({ type: 'SET_FILTER', filter }),
            setView: payload => dispatch({ type: 'SET_VIEW', payload }),
            resetView: () => dispatch({ type: 'RESET_VIEW' }),
        }),
        []
    );

    return { state, actions };
};
